import React, { useState } from "react";
import { AxiosResponse } from "axios";
import { getRequestUpdates } from "apis/myRequestsService";
import { useToastStore } from "store/toastStore";


interface RequestUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  flightNo?: string;
  createdDate?: string;
  isBulkOperation?: boolean;
  requestIds?: number[];
}

const RequestUpdateModal: React.FC<RequestUpdateModalProps> = ({ 
  isOpen, 
  onClose, 
  flightNo = "N/A", 
  createdDate = "",
  isBulkOperation = false,
  requestIds = []
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  // Format date for display
  const formattedDate = createdDate ? (() => {
    const date = new Date(createdDate);
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  })() : "N/A";

  // Create a handler for modal interactions that stops event propagation
  const handleModalInteraction = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Wrap the onClose with stopPropagation
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setError('');
    onClose();
  };

  // Handle the request update action
  const handleRequestUpdate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLoading(true);
    setError(null);
    
    try {
      // If it's a single operation and no requestIds provided, we should have a way to get the ID
      // This is just a placeholder - you'll need to adjust based on your actual data structure
      const ids = isBulkOperation ? 
        requestIds : 
        (requestIds.length > 0 ? requestIds : [0]); // Replace 0 with actual ID for single operation
      
      const response = await getRequestUpdates(ids);
      useToastStore.getState().showToast("Update request successful","success",3000);
      onClose();
    } catch (err) {
      setError('Failed to request update. Please try again.');
      useToastStore.getState().showToast("Failed to request update. Please try again.","error",3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      id="default-modal"
      tabIndex={-1}
      aria-hidden="true"
      className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-screen bg-black bg-opacity-50"
      onClick={handleModalInteraction}
    >
      <div className="relative p-4 w-full max-w-xl" onClick={handleModalInteraction}>
        {/* Modal content */}
        <div className="relative bg-white rounded-lg shadow" onClick={handleModalInteraction}>
          {/* Modal header */}
          <div className="flex items-center justify-between p-4 md:p-5 rounded-t">
            <h3 className="text-lg font-semibold text-grease-100">
              Request Update
            </h3>
            <button
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={handleClose}
              disabled={isLoading}
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          {/* Modal body */}
          <div className="p-4 border-t">
            {isBulkOperation ? (
              <p className="text-sm text-grease-200">
                You are about to request an update for{" "}
                <span className="font-semibold">{requestIds.length} selected flights</span>.
                Do you want to proceed?
              </p>
            ) : (
              <p className="text-sm text-grease-200">
                You are about to request an update for flight{" "}
                <span className="font-semibold">{flightNo}</span> on{" "}
                <span className="font-semibold">{formattedDate}</span>. Do you want
                to proceed?
              </p>
            )}
            <div className="bg-indigo-50 rounded-lg p-5 flex gap-4 mt-5">
              <i className="st-info text-2xl text-royalblue-100"></i>
              <div>
                <p className="text-sm text-grease-200">
                  {isBulkOperation 
                    ? "The selected flights' availability information was last updated recently."
                    : "The availability of this flight has been updated 32 minutes ago."
                  } Are you sure you want to proceed?
                </p>
                <p className="text-sm text-grease-200 font-bold mt-4">
                  {isBulkOperation
                    ? `Requesting updates costs ${requestIds.length} credit${requestIds.length !== 1 ? 's' : ''}.`
                    : "Requesting an update costs 1 credit."
                  }
                </p>
              </div>
            </div>
            {error && (
              <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {error}
              </div>
            )}
          </div>
          <div className="flex gap-4 items-center justify-end p-4 md:p-5 border-t border-gray-200 rounded-b">
            <button
              className="flex items-center gap-2 p-3 bg-indigo-100 rounded-lg"
              onClick={handleClose}
              disabled={isLoading}
            >
              <p className="font-medium bg-skyblue-200 text-grease-50 text-sm">
                Cancel
              </p>
            </button>
            <button 
              className={`flex items-center gap-2 p-3 ${isLoading ? 'bg-gray-400' : 'bg-royalblue-100'} rounded-lg`}
              onClick={handleRequestUpdate}
              disabled={isLoading}
            >
              <p className="font-medium text-white text-sm">
                {isLoading ? "Processing..." : "Request Update"}
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestUpdateModal;